import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { clientLogger } from '#app/utils/pz/logger/clientLogger'
import { publicRoutes } from '#app/utils/pz/routes'

const log = clientLogger.scoped('CookieBanner')

/**
 * Hook para gestionar el consentimiento de cookies
 * @param initialConsent Estado inicial del consentimiento
 * @returns Objeto con el estado y funciones para gestionar el consentimiento
 */
export function useCookieConsent(initialConsent: boolean) {
	const [consentGiven, setConsentGiven] = useState<boolean>(initialConsent)

	// Manejadores para el consentimiento de cookies
	const handleAccept = () => {
		setConsentGiven(true)
		// Guardar en cookie
		document.cookie = `cookieConsent=true;path=/;max-age=${365 * 24 * 60 * 60};SameSite=Lax`
	}

	const handleDecline = () => {
		setConsentGiven(false)
		// Guardar en cookie
		document.cookie = `cookieConsent=false;path=/;max-age=${365 * 24 * 60 * 60};SameSite=Lax`
	}

	return {
		consentGiven,
		handleAccept,
		handleDecline,
	}
}

function hasCookieConsentDecision() {
	if (typeof document === 'undefined') return false
	return document.cookie
		.split(';')
		.map((cookie) => cookie.trim().split('=')[0])
		.includes('cookieConsent')
}

/**
 * Componente de banner de consentimiento de cookies
 * @param props Propiedades del componente
 * @returns Componente React
 */
export function CookieBannerComponent({
	onAccept,
	onDecline,
	children,
}: {
	onAccept: () => void
	onDecline: () => void
	children?: React.ReactNode
}) {
	const { t } = useTranslation()
	const [visible, setVisible] = useState(() => !hasCookieConsentDecision())
	const message = t('common.cookies.message')
	const privacyPolicy = t('common.cookies.privacyPolicy')
	const forMoreInfo = t('common.cookies.forMoreInfo')
	const acceptLabel = t('common.cookies.accept')
	const declineLabel = t('common.cookies.decline')

	if (!visible) return null

	const accept = () => {
		onAccept()
		setVisible(false)
	}
	const decline = () => {
		onDecline()
		setVisible(false)
	}

	return (
		<section
			aria-label={t('common.cookies.bannerLabel', 'Aviso de cookies')}
			className="border-border bg-card/95 text-card-foreground fixed inset-x-0 bottom-0 z-50 flex flex-col gap-4 border-t px-6 py-4 text-sm shadow-[0_-4px_16px_rgba(0,0,0,0.15)] backdrop-blur md:flex-row md:items-center md:justify-between"
		>
			<p className="m-0">
				{children || (
					<>
						{message}{' '}
						<Link
							to={`${publicRoutes.legal()}/privacy`}
							className="text-primary font-semibold underline underline-offset-4"
						>
							{privacyPolicy}
						</Link>{' '}
						{forMoreInfo}
					</>
				)}
			</p>
			<div className="flex shrink-0 flex-wrap gap-2">
				<button
					type="button"
					aria-label={acceptLabel}
					onClick={accept}
					className="bg-primary text-primary-foreground rounded-md px-5 py-2 text-sm font-medium"
				>
					{acceptLabel}
				</button>{' '}
				<button
					type="button"
					aria-label={declineLabel}
					onClick={decline}
					className="border-border text-muted-foreground rounded-md border px-5 py-2 text-sm font-medium"
				>
					{declineLabel}
				</button>
			</div>
		</section>
	)
}

/**
 * Función para extraer el consentimiento de cookies de la solicitud HTTP
 * @param request Objeto Request de la solicitud HTTP
 * @returns Estado del consentimiento de cookies
 */
export function extractCookieConsent(request: Request): boolean {
	// Comprobar si hay consentimiento de cookies existente
	const cookieHeader = request.headers.get('Cookie')
	const cookieConsent = cookieHeader
		? cookieHeader
				.split(';')
				.map((cookie: string) => cookie.trim().split('='))
				.find((parts: string[]) => parts[0] === 'cookieConsent')?.[1]
		: undefined

	// Parsear el valor de la cookie si existe
	let consentGiven = false

	try {
		if (cookieConsent) {
			consentGiven = cookieConsent === 'true'
		}
	} catch (e) {
		log.error(
			'Error al parsear la cookie de consentimiento',
			e instanceof Error ? e : { error: String(e) },
		)
	}

	return consentGiven
}
